import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { X, Save, Shield, Mail, User as UserIcon } from "lucide-react";

const UserEditModal = ({ user, onClose, onSave, loading }) => {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    two_factor_enabled: false
  });

  useEffect(() => {
    if (user) {
      setFormData({
        full_name: user.full_name || "",
        email: user.email || "",
        two_factor_enabled: user.two_factor_enabled || false
      });
    }
  }, [user]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(user.id, formData);
  };

  if (!user) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-t-lg flex justify-between items-center">
          <h2 className="text-2xl font-bold">Edit User Details</h2>
          <button
            onClick={onClose}
            className="hover:bg-white/20 rounded-full p-2 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* User Info Section */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <UserIcon className="w-5 h-5 text-blue-600" />
              User Information
            </h3>
            <div className="grid gap-4">
              <div>
                <Label htmlFor="user-id" className="text-sm text-gray-600">User ID</Label>
                <Input
                  id="user-id"
                  value={user.id}
                  disabled
                  className="bg-gray-100 font-mono text-sm"
                />
              </div>
              
              <div>
                <Label htmlFor="created-at" className="text-sm text-gray-600">Created At</Label>
                <Input
                  id="created-at"
                  value={new Date(user.created_at).toLocaleString()}
                  disabled
                  className="bg-gray-100"
                />
              </div>

              {user.license_key && (
                <div>
                  <Label htmlFor="license-key" className="text-sm text-gray-600">License Key</Label>
                  <Input
                    id="license-key"
                    value={user.license_key}
                    disabled
                    className="bg-gray-100 font-mono text-xs"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Editable Fields */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name" className="flex items-center gap-2">
                <UserIcon className="w-4 h-4 text-blue-600" />
                Full Name
              </Label>
              <Input
                id="edit-name"
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder="Enter full name"
                required
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="edit-email" className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-blue-600" />
                Email Address
              </Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="Enter email address"
                required
                className="mt-1"
              />
            </div>
          </div>

          {/* 2FA Toggle Section */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-800">Two-Factor Authentication</h3>
                </div>
                <p className="text-sm text-gray-700 mb-2">
                  {formData.two_factor_enabled
                    ? `This user has 2FA enabled. They will receive verification codes at ${user.email} when logging in.`
                    : "This user does not have 2FA enabled. Enable it to add an extra layer of security."}
                </p>
                <div className={`p-3 rounded-md ${formData.two_factor_enabled ? 'bg-green-100 border border-green-300' : 'bg-amber-100 border border-amber-300'}`}>
                  <p className="text-xs font-semibold mb-1 ${formData.two_factor_enabled ? 'text-green-800' : 'text-amber-800'}">
                    {formData.two_factor_enabled ? '✓ 2FA Enabled' : '⚠️ 2FA Disabled'}
                  </p>
                  <p className="text-xs ${formData.two_factor_enabled ? 'text-green-700' : 'text-amber-700'}">
                    {formData.two_factor_enabled
                      ? `Verification codes will be sent to ${user.email}`
                      : 'Only email and password required for login'}
                  </p>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2">
                <Switch
                  checked={formData.two_factor_enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, two_factor_enabled: checked })}
                  disabled={loading}
                />
                <span className="text-xs text-gray-600 text-center">
                  {formData.two_factor_enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserEditModal;
